'use client';

import { cn } from '@zealot/ui/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './button';
import { Text, type textVariants } from './text';

// gloss
type ExpandableTextProps = Omit<VariantProps<typeof textVariants>, 'lineClamp'> & {
  children: React.ReactNode;
  lines?: 1 | 2 | 3 | 4;
  moreLabel?: string;
  lessLabel?: string;
  togglePlacement?: 'below' | 'end';
  className?: string;
};

// gloss
const ExpandableText = ({
  children,
  lines = 4,
  moreLabel = 'Show more',
  lessLabel = 'Show less',
  togglePlacement = 'below',
  className,
  ...textProps
}: ExpandableTextProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = textRef.current;
    if (!element || isExpanded) return;

    const measure = () => setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [isExpanded, children]);

  return (
    <div data-slot="expandable-text">
      <Text ref={textRef} lineClamp={isExpanded ? 'none' : lines} className={className} {...textProps}>
        {children}
      </Text>

      {isOverflowing && (
        <div className={cn('flex', togglePlacement === 'end' && 'justify-end')}>
          <Button
            data-slot="expandable-text-toggle"
            variant="link"
            size="sm"
            className={cn('px-0', togglePlacement === 'end' ? 'h-auto' : 'mt-1')}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? lessLabel : moreLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export type { ExpandableTextProps };
export { ExpandableText };
